import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useRef } from "react";

const EASE = [0.16, 1, 0.3, 1];

// Line-by-line masked reveal for headings
export const KineticText = ({ lines = [], className = "", delay = 0, testId }) => {
  return (
    <span className={className} data-testid={testId}>
      {lines.map((line, i) => (
        <span key={i} className="mask-line">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: delay + i * 0.09 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

// Scroll-triggered reveal wrapper
export const Reveal = ({ children, className = "", delay = 0, y = 40, testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px -10% 0px" });
  return (
    <motion.div
      ref={ref}
      data-testid={testId}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
};

export const SectionLabel = ({ children, className = "" }) => (
  <span className={`font-mono text-xs uppercase tracking-[0.25em] text-raritan-muted flex items-center gap-3 ${className}`}>
    <span className="inline-block w-6 h-px bg-raritan-red" />
    {children}
  </span>
);

// Animated counter
export const Counter = ({ value, suffix = "", className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: 1600, bounce: 0 });

  useEffect(() => {
    if (inView) mv.set(value);
  }, [inView, value, mv]);

  useEffect(() => {
    const el = ref.current;
    return spring.on("change", (v) => {
      if (el) el.textContent = Math.round(v) + suffix;
    });
  }, [spring, suffix]);

  return <span ref={ref} className={className}>0{suffix}</span>;
};
